import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { useMemo } from 'react'
import { Inbox } from 'react-feather'
import styled from 'styled-components/macro'

import IncentivePositionList from '../../components/IncentivePositionList'
import { useIncentivePositions } from '../../hooks/useIncentivePositions'
import { LoadingRows } from '../../pages/Pool/styleds'
import { useToggleWalletModal } from '../../state/application/hooks'
import { ThemedText } from '../../theme/components/text'
import { ButtonPrimary } from '../Button'

const EmptyPositions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  min-height: 200px;
`

const InboxIcon = styled(Inbox)`
  width: 48px;
  height: 48px;
  stroke-width: 1px;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.textTertiary};
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
  width: 100%;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
`

function PositionsLoadingPlaceholder() {
  return (
    <LoadingRows>
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
    </LoadingRows>
  )
}

interface IncentivePositionsProps {
  incentiveId: string | undefined
}

export default function IncentivePositions({ incentiveId }: IncentivePositionsProps) {
  const { account } = useWeb3React()
  const toggleWalletModal = useToggleWalletModal()

  // Get positions filtered by incentive pool
  const { positions, loading } = useIncentivePositions(incentiveId)

  // Get only open positions
  const openPositions = useMemo(() => {
    if (!positions) return []

    return positions.filter((position) => !position.liquidity?.isZero())
  }, [positions])

  if (loading) {
    return <PositionsLoadingPlaceholder />
  }

  if (!account) {
    return (
      <ContentWrapper>
        <EmptyPositions>
          <InboxIcon />
          <ThemedText.BodySecondary textAlign="center" marginBottom="16px">
            <Trans>Connect your wallet to view your positions</Trans>
          </ThemedText.BodySecondary>
          <ButtonPrimary onClick={toggleWalletModal} padding="8px 16px">
            <Trans>Connect Wallet</Trans>
          </ButtonPrimary>
        </EmptyPositions>
      </ContentWrapper>
    )
  }

  if (openPositions.length === 0) {
    return (
      <ContentWrapper>
        <EmptyPositions>
          <InboxIcon />
          <ThemedText.BodySecondary textAlign="center">
            <Trans>You don&apos;t have any open positions in this pool</Trans>
          </ThemedText.BodySecondary>
        </EmptyPositions>
      </ContentWrapper>
    )
  }

  return (
    <ContentWrapper>
      <IncentivePositionList positions={openPositions} />
    </ContentWrapper>
  )
}
